import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { SimulationConfig } from './types';

interface SimulationHUDProps {
  elapsedTime: number;
  timeScale: number;
  ballCount: number;
  bounceCount: number;
  ringsLeft: number;
  timeRemaining: number;
  currentRound: number;
  containerRadius: number;
  ballRadius: number;
  gravity: number;
  restitution: number;
  config: SimulationConfig;
}

export const SimulationHUD: React.FC<SimulationHUDProps> = ({ 
  elapsedTime, timeScale, ballCount, bounceCount, ringsLeft, 
  timeRemaining, currentRound, containerRadius, ballRadius, 
  gravity, restitution, config 
}) => {
  const [fps, setFps] = useState(0);
  
  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();
    let animationFrameId: number;

    const updateFps = () => {
      frameCount++;
      const now = performance.now();
      if (now - lastTime >= 1000) {
        setFps(Math.round((frameCount * 1000) / (now - lastTime)));
        frameCount = 0;
        lastTime = now;
      }
      animationFrameId = requestAnimationFrame(updateFps);
    };

    animationFrameId = requestAnimationFrame(updateFps);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  if (!config.showHUD) return null;

  const positionClasses = {
    TOP_LEFT: 'top-8 left-8 items-start',
    TOP_RIGHT: 'top-8 right-8 items-end',
    BOTTOM_LEFT: 'bottom-8 left-8 items-start',
    BOTTOM_RIGHT: 'bottom-8 right-8 items-end pb-16'
  };

  const hudItems = [
    { show: config.hudShowElapsedTime, label: 'Elapsed', value: `${(elapsedTime/1000).toFixed(2)}s` },
    { show: config.hudShowSpeed, label: 'Speed', value: `${timeScale.toFixed(2)}x` },
    { show: config.hudShowBallCount, label: 'Spheres', value: ballCount },
    { show: config.hudShowBounceCount, label: 'Bounces', value: bounceCount },
    { show: config.hudShowRingsLeft, label: 'Rings', value: ringsLeft, mode: 'ESCAPE' },
    { show: config.hudShowRingsLeft, label: 'Rings', value: ringsLeft, mode: 'RHYTHMIC_ESCAPE' },
    { show: config.hudShowTimeRemaining, label: 'Time', value: `${timeRemaining.toFixed(1)}s`, mode: 'ESCAPE' },
    { show: config.hudShowTimeRemaining, label: 'Time', value: `${timeRemaining.toFixed(1)}s`, mode: 'RHYTHMIC_ESCAPE' },
    { show: config.hudShowFPS, label: 'FPS', value: fps },
    { show: config.hudShowCurrentRound, label: 'Round', value: currentRound, mode: 'SPACE_SPIRAL' },
    { show: config.hudShowContainerRadius, label: 'Radius', value: `${(config.mode === 'RHYTHMIC_ESCAPE' ? config.rhythmicEscapeShieldRingRadius : containerRadius).toFixed(0)}px` },
    { show: config.hudShowBallRadius, label: 'Ball Size', value: `${ballRadius.toFixed(1)}px` },
    { show: config.hudShowGravity, label: 'Gravity', value: gravity.toFixed(4) },
    { show: config.hudShowRestitution, label: 'Bounciness', value: restitution.toFixed(2) },
  ].filter(item => item.show && (!item.mode || config.mode === item.mode));

  if (hudItems.length === 0) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: config.hudOpacity }}
      className={`absolute z-40 pointer-events-none flex flex-col gap-2.5 transition-all duration-300 ${positionClasses[config.hudPosition]}`}
      style={{ 
        transform: `scale(${config.hudScale})`,
        transformOrigin: config.hudPosition.includes('LEFT') ? 'left top' : 'right top'
      }}
    >
      {hudItems.map((item, idx) => (
        <motion.div 
          key={item.label}
          initial={{ x: config.hudPosition.includes('LEFT') ? -20 : 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: idx * 0.05 }}
          className="hardware-surface glow-active px-5 py-2.5 flex flex-col items-center min-w-[110px] sm:min-w-[130px]"
        >
          <span className="text-[10px] font-black uppercase tracking-widest opacity-60">{item.label}</span>
          <span className="text-xl font-mono font-bold tracking-tight">{item.value}</span>
        </motion.div>
      ))}
    </motion.div>
  );
};
